import { isAdState, playerControlsState, playerTimeState } from '@audacy-clients/core/atoms/player';
import { isPlayerButtonVisible } from '@audacy-clients/core/utils/player';
import { useRecoilValue } from 'recoil';

import usePlayerControls from '~/components/Player/hooks/usePlayerControls';
import PlayerConstants from '~/constants/player';
import type { TStyles } from '~/types/emotion-styles';

import { Colors } from '~/styles';
import Scrubber from './Scrubber';
import { useScrubberProps } from './hooks/useScrubberProps';

interface IScrubberWithState {
  inputCss?: TStyles;
  isFullPlayer?: boolean;
  shouldDisplayTime?: boolean;
  bufferProgress?: number; // 0 to 1
  playProgress?: number; // 0 to 1
  shouldDisplayScrubber?: boolean;
  hideBackToLiveButton?: boolean;
}

const ScrubberWithState = ({
  bufferProgress,
  inputCss,
  isFullPlayer,
  playProgress,
  shouldDisplayTime,
  shouldDisplayScrubber = true,
  hideBackToLiveButton = false,
}: IScrubberWithState): JSX.Element => {
  const { onSkip, onSkipToLive } = usePlayerControls();

  const {
    customEnd,
    onScrubActionEnd,
    onScrubActionStart,
    onScrubAction,
    playProgressValue,
    scrubToTime,
    currentTime,
    isScrubbing,
    hasUnavailableLiveGap,
  } = useScrubberProps({ playProgress });

  const isAd = useRecoilValue(isAdState);

  const {
    backToLiveButton,
    ff15sButton,
    rw15sButton,
    scrubberIsUserControlled,
    shouldDisplayLiveIndicator,
  } = useRecoilValue(playerControlsState) ?? {};

  const canScrub =
    shouldDisplayScrubber &&
    isPlayerButtonVisible(ff15sButton) &&
    isPlayerButtonVisible(rw15sButton) &&
    !isAd;

  const shouldDisplayBackToLive = isPlayerButtonVisible(backToLiveButton, true) && !isAd;

  const noop = () => {
    // do nothing
  };

  const {
    duration = 0,
    offset = 0,
    playbackLiveFraction = 0,
    availableContentFraction,
  } = useRecoilValue(playerTimeState);

  // [OOP-741] Gapped: fill to received-content end (full-episode fraction).
  const bufferProgressValue =
    bufferProgress ?? (hasUnavailableLiveGap ? availableContentFraction : playbackLiveFraction);

  return (
    <Scrubber
      bufferedValue={bufferProgressValue}
      currentTime={isScrubbing ? offset : currentTime}
      customEnd={customEnd}
      duration={duration}
      inputCss={inputCss}
      isFullPlayer={isFullPlayer}
      playProgressValue={playProgressValue}
      onSkipForward={() => onSkip(PlayerConstants.skip15)}
      onSkipBack={() => onSkip(PlayerConstants.skip15 * -1)}
      onSkipToLive={onSkipToLive}
      onScrubActionEnd={canScrub ? onScrubActionEnd : noop}
      onScrubActionStart={canScrub ? onScrubActionStart : noop}
      onScrubAction={onScrubAction}
      scrubberIsUserControlled={scrubberIsUserControlled}
      shouldDisplayTime={shouldDisplayTime && !isAd}
      shouldDisplayBackToLive={shouldDisplayBackToLive && !hideBackToLiveButton}
      shouldDisplayLiveIndicator={shouldDisplayLiveIndicator && !isAd}
      scrubToTime={scrubToTime}
      shouldDisplayScrubber={canScrub}
      progressBarColor={hasUnavailableLiveGap ? Colors.transparent : Colors.white}
      isThumbAlwaysVisible={hasUnavailableLiveGap}
    />
  );
};

export default ScrubberWithState;
